import { ReactNode } from 'react';
import { BUTTON_SIZES, BUTTON_VARIANTS } from 'ui/button/constants';
import { ChevronLeft24 } from 'ui/icons/aurora/24px/ChevronLeft24';
import { Close24 } from 'ui/icons/action/Close24';
import { NegativeFillDiamond } from 'ui/icons/status/NegativeFillDiamond';
import { WarningFill16 } from 'ui/icons/aurora/16px/WarningFill16';
import styled from 'styled-components';
import { IconButton } from 'ui/button';
import { colors, heading4, spacing } from 'ui/theme';
import { MODAL_VARIANTS, ModalSize, ModalVariant } from './Modal.constants';
import { DataSet } from './types';
import { useModalContext } from './ModalContext';
import { MODAL_HEADER_STYLES, ModalTitle } from './Modal.styled';

const Header = styled.div<{ size?: ModalSize; variant?: ModalVariant }>`
  ${heading4};
  align-items: center;
  display: flex;
  flex: 0 0 auto;
  gap: ${spacing.half};
  justify-content: space-between;
  padding: ${spacing.base} ${spacing.base} ${spacing.threeQuarter} ${spacing.plusHalf};
  border-radius: ${spacing.threeQuarter} ${spacing.threeQuarter} 0 0;
  ${({ size }) => (size && MODAL_HEADER_STYLES[size]) || ``};
  ${({ variant }) => (variant && MODAL_HEADER_STYLES[variant]) || ``};

  path {
    fill: ${({ variant }) => {
      if (variant === MODAL_VARIANTS.DELETE) {
        return colors.iconDestructive;
      } else if (variant === MODAL_VARIANTS.WARNING) {
        return colors.iconWarning;
      } else {
        return '';
      }
    }};
  }
`;

type CloseButtonProps = { modalSize?: ModalSize };
const CloseButton = styled(IconButton)<CloseButtonProps>`
  ${({ modalSize }) => modalSize || ``};
`;

export type ModalHeaderProps = {
  /* Header content */
  children: ReactNode;
  /** Modal id */
  id?: string;
  /** Sets focus on close button */
  autoFocus?: boolean;
  /** Allows for optional override of onClose */
  onClose?: () => void;
  /** Allows for optional override of size */
  size?: ModalSize;
  /** Allows for optional override of variant */
  variant?: ModalVariant;
};

export const ModalHeader = ({
  children,
  autoFocus = true,
  onClose: onCloseProp,
  size: sizeProp,
  variant: variantProp,
  ...props
}: ModalHeaderProps) => {
  const { onClose: onCloseContext, size: sizeContext, variant: variantContext } = useModalContext();
  const onClose = onCloseProp ?? onCloseContext;
  const size = sizeProp ?? sizeContext;
  const variant = variantProp ?? variantContext;
  return (
    <Header
      id="header"
      size={size}
      variant={variant}
      onClick={(e) => e.stopPropagation()}
      data-test={DataSet.ModalHeaderTestId}
      {...props}
    >
      {variant === MODAL_VARIANTS.DELETE && <NegativeFillDiamond height={24} width={24} />}
      {variant === MODAL_VARIANTS.WARNING && <WarningFill16 height={24} width={24} />}
      {variant === MODAL_VARIANTS.INNER_PAGE && (
        <IconButton size={BUTTON_SIZES.LARGE} variant={BUTTON_VARIANTS.GHOST}>
          <ChevronLeft24 />
        </IconButton>
      )}
      <ModalTitle>{children}</ModalTitle>
      <CloseButton
        autoFocus={autoFocus}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          onClose();
        }}
        size={BUTTON_SIZES.LARGE}
        modalSize={size}
        variant={BUTTON_VARIANTS.GHOST}
      >
        <Close24 />
      </CloseButton>
    </Header>
  );
};
